/* Module of functions for interacting with the AccuFlex™ Connect Service.
 * All functions return promises that resolve to JSON objects.
 * See the AccuFlex™ Connect API documentation for more information:
 * https://github.com/TaylorTechnologies/XFConnectJS/blob/b65c5c60e6283e897bd3f1ad32d78d094839d122/Xpress%20Flex%20API%20Integration.docx
 */
import {ScanningErrors} from 'services/errorCodes';
import {xfcBromineSanitizerValue} from 'ui/helpers/constants';

class PromiseQueue {
  constructor() {
    this.queue = [];
  }

  queuePromise(promise) {
    return new Promise((resolve, reject) => {
      const task = async () => {
        try{
          resolve(await promise());
        }catch(err){
          reject(err);
        }
      }
      this.queue.push(task);
      this.runQueue();
    });
  }

  async runQueue() {
    if (this.queue.length !== 1) return;
    while (this.queue.length > 0) {
      await this.queue[0]();
      this.queue.shift();
    }
  }
}

const promiseQueue = new PromiseQueue();
let XFCONNECT_SERVICE_ENDPOINT = 'http://localhost:61264/xf2/v1';


/**
 * Throws a service offline error for the XFC Service.
 *
 * @param {any} err Error
 * @throws Will throw the XFC Service Offline error
 * @returns {void}
 */
function _throwServiceOffline(err) {
  console.error(err);
  throw {code: ScanningErrors.XFC_SERVICEOFFLINE, error: err};
}


/**
 * Calls the XFC Service 'About' endpoint to get device and server status information
 *
 * @return {Promise} XFCAbout Promise
 */
export function XFCAbout() {
  return promiseQueue.queuePromise(() => fetch(XFCONNECT_SERVICE_ENDPOINT + '/about', {cache: 'no-store'})
           .catch(_throwServiceOffline)
           .then(response => {
             return response.json();
           }));
}

/**
 * Calls the XFC Service 'Initialize' endpoint to reset the scanner and prep for new scan
 *
 * @return {Promise} XFCInit Promise
 */
export function XFCInit() {
  return promiseQueue.queuePromise(() => fetch(XFCONNECT_SERVICE_ENDPOINT + '/initialize', {cache: 'no-store'})
           .catch(_throwServiceOffline)
           .then(response => response.json()));
}

/**
 * Calls the XFC Service 'New Test' endpoint to start the water test scanning
 *
 * @param {Boolean} isBromineScan Determines if it's a bromine sanitizer water test or not
 * @return {Promise} XFCScan Promise
 */
export function XFCScan(isBromineScan = false) {
  if (isBromineScan) {
    return promiseQueue.queuePromise(() => fetch(XFCONNECT_SERVICE_ENDPOINT + '/newtest?sanitizer=' + xfcBromineSanitizerValue, {cache: 'no-store'})
    .catch(_throwServiceOffline)
    .then(response => response.json()));
  }
  return promiseQueue.queuePromise(() => fetch(XFCONNECT_SERVICE_ENDPOINT + '/newtest', {cache: 'no-store'})
           .catch(_throwServiceOffline)
           .then(response => response.json()));
}

/**
 * Calls the XFC Service 'Status' endpoint to get the current status of the device/scanning
 *
 * @return {Promise} XFCStatus Promise
 */
export function XFCStatus() {
  return promiseQueue.queuePromise(() => fetch(XFCONNECT_SERVICE_ENDPOINT + '/status', {cache: 'no-store'})
           .catch(_throwServiceOffline)
           .then(response => response.json()));
}

/**
 * Calls the XFC Service 'Inventory' endpoint to get current inventory status of XF scanner
 * If params given, it will update the relevant flex strips code
 * @param {Array<string>} updates 4-element array of new flex strip code values
 * @return {Promise} XFCInventory Promise
 */
export function XFCInventory(updates = undefined) {
  if (updates && updates.length === 4) {
    return promiseQueue.queuePromise(() => fetch(XFCONNECT_SERVICE_ENDPOINT + '/inventory?' + 'Flex1=' + updates[0] + '&Flex2=' + updates[1] + '&Flex3=' + updates[2] + '&Flex4=' + updates[3],
      {cache: 'no-store'})
    .catch(_throwServiceOffline)
    .then(response => response.json()));
  }

  return promiseQueue.queuePromise(() => fetch(XFCONNECT_SERVICE_ENDPOINT + '/inventory', {cache: 'no-store'})
  .catch(_throwServiceOffline)
  .then(response => response.json()));
}